import DiscountTicketBackground from 'assets/images/discount/discount_ticket_background.png';
import DiscountTicketBackgroundComing from 'assets/images/discount/discount_ticket_background_coming_soon.png';
import DiscountTicketBackgroundDisabled from 'assets/images/discount/discount_ticket_background_disabled.png';
import NoteBlueIco from 'assets/images/discount/note_blue_ico.svg';
import NoteGreyIco from 'assets/images/discount/note_grey_ico.svg';
import NoteWhiteIco from 'assets/images/discount/note_white_ico.svg';
import BuyIcon from 'assets/ver2/icons/header/buy-currency-icon.svg';
import SendIcon from 'assets/ver2/icons/header/remittance-icon.svg';
import { handleApplyDiscount as handleApplyDiscountDefault } from 'components/Discount/func';
import ButtonCommon from 'components/common/ButtonCommon';
import SizeBox from 'components/common/SizeBox';
import {
  DISCOUNT_ACTIVE,
  DISCOUNT_COMING_SOON,
  DISCOUNT_EXPIRED,
  DISCOUNT_FULLY_REDEEMED,
  DISCOUNT_REDEEMED,
  EN_LANG,
  FX_TRANSACTION_ADD_PREFIX,
  MOBILE_SMALL_MAX_WIDTH,
  MOBILE_SMALL_MIN_WIDTH,
  OFFERS_PREFIX,
  ORDER_EMAIL,
  TRANSACTION_ADD_PREFIX,
} from 'constants';
import {
  chipStyles,
  datePicker,
  format,
  getChipValue,
  getDiscountStatus,
  getDiscountUnit,
  getValidText,
  textColor,
  textTicketColor,
} from 'helpers';
import Cookies from 'helpers/cookies';
import { MAKE_PAYMENT_KEY } from 'hooks/form-add-transaction/useAddTransaction';
import useFormAddTransactionStore from 'hooks/form-add-transaction/useFormAddTransactionStore';
import { FX_MAKE_PAYMENT_KEY } from 'hooks/fx/useFxAddTransaction';
import useFxFormAddTransactionStore from 'hooks/fx/useFxFormAddTransactionStore';
import useAuth from 'hooks/useAuth';
import useAutoScaleToFitContainer from 'hooks/useAutoScaleToFitContainer';
import useLang from 'hooks/useLang';
import usePage from 'hooks/usePage';
import useQuery from 'hooks/useQuery';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { paragraphMixin, textEllipsisSecondLineMixin } from 'styles';
import { domUtils, stringUtils } from 'utils';
import { DiscountModal } from '../DiscountModal/ver2';
import CircleRoundCut from './CircleRoundCut';
import useStore from 'hooks/useStore';
import MarkedIcon from 'assets/ver2/icons/marked-icon.svg';

const COL_RIGHT_WIDTH = 85;

const DiscountTicket = ({
  item,
  inactive,
  isTriggerShowDiscountModal,
  setTriggerDiscountModal = () => {},
  onApplyError,
  handleApplyDiscount,
  styles = {},
}) => {
  const { t, locate = EN_LANG } = useLang();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { token } = useAuth();
  const query = useQuery();
  const discountCode = query.get('discountCode');
  const { isOffersVouchersPage } = usePage();
  const { resetSendMoneyFlow } = useFormAddTransactionStore();

  const [isOpenDiscountModal, setOpenDiscountModal] = useState(false);

  const isCreateTxn = pathname === `${TRANSACTION_ADD_PREFIX}`;

  const {
    code,
    discountMethod,
    discountAmount,
    startDate = 0,
    endDate = 0,
    redeem = 0,
    limitTxnPerCustomer = 0,
    lastRedeemAt = 0,
    descriptions,
    serviceType,
  } = item || {};
  const { shortDescription } = descriptions?.[locate] || {};
  const {
    status,
    discountMethodKey,
    isActive,
    isActiveUnlimited,
    isDiscountActiveUsedHidden,
    isComingSoon,
  } = getDiscountStatus(item) || {};

  const isActiveAndAvailable = isActive && !inactive;

  const discountAmountUnitClassName = `discount-ticket-amount-unit-${code}`;
  useAutoScaleToFitContainer({
    className: discountAmountUnitClassName,
  });

  const isFx = serviceType === 'FX';

  const { resetBuyMoneyFlow, setTransactionDetails } =
    useFxFormAddTransactionStore();

  useEffect(() => {
    if (!isOffersVouchersPage) return;
    if (!discountCode) return;
    if (!isTriggerShowDiscountModal) return;
    if (item?.code === discountCode) {
      setOpenDiscountModal(true);
      setTriggerDiscountModal(false);
    }
  }, [isOffersVouchersPage, discountCode, item, isTriggerShowDiscountModal]);

  const handleOnClick = () => {
    Cookies.remove(MAKE_PAYMENT_KEY);
    Cookies.remove(FX_MAKE_PAYMENT_KEY);
    Cookies.remove(ORDER_EMAIL);
    if (isFx) {
      resetBuyMoneyFlow();
      setTransactionDetails(null);
      navigate(`${FX_TRANSACTION_ADD_PREFIX}`);
    } else {
      resetSendMoneyFlow();
      navigate(`${TRANSACTION_ADD_PREFIX}`);
    }
  };

  const handleOnCloseDiscountModal = () => {
    setOpenDiscountModal(false);
    domUtils.replaceUrlWithoutReload({ path: OFFERS_PREFIX });
  };

  const handleOnOpenDiscountModal = () => {
    setOpenDiscountModal(true);
  };

  const discountTicketBackground = {
    [DISCOUNT_ACTIVE]: DiscountTicketBackground,
    [DISCOUNT_COMING_SOON]: DiscountTicketBackgroundComing,
    [DISCOUNT_REDEEMED]: DiscountTicketBackgroundDisabled,
    [DISCOUNT_EXPIRED]: DiscountTicketBackgroundDisabled,
    [DISCOUNT_FULLY_REDEEMED]: DiscountTicketBackgroundDisabled,
  };

  const noteIco = {
    [DISCOUNT_ACTIVE]: (
      <NoteIcon src={NoteWhiteIco} onClick={handleOnOpenDiscountModal} />
    ),
    [DISCOUNT_COMING_SOON]: (
      <NoteIcon src={NoteBlueIco} onClick={handleOnOpenDiscountModal} />
    ),
    [DISCOUNT_REDEEMED]: (
      <NoteIcon src={NoteGreyIco} onClick={handleOnOpenDiscountModal} />
    ),
    [DISCOUNT_EXPIRED]: (
      <NoteIcon src={NoteGreyIco} onClick={handleOnOpenDiscountModal} />
    ),
    [DISCOUNT_FULLY_REDEEMED]: (
      <NoteIcon src={NoteGreyIco} onClick={handleOnOpenDiscountModal} />
    ),
  };

  const { state } = useStore();
  let { addTransactionInformation } = state;
  const { paymentMethod } = addTransactionInformation || {};
  const { discountApplied } = paymentMethod || {};
  const isDiscountAppliedMarked = isCreateTxn && discountApplied?.code === code;

  const renderButtonApply = () => {
    if (isDiscountAppliedMarked)
      return <img src={MarkedIcon} width={30} height={30} alt="" />;
    return (
      <ButtonCommon
        value={t('button_apply')}
        color="var(--c-primary)"
        background="var(--bg-primary)"
        isFill={true}
        styles={{
          width: '76px',
          height: '32px',
          paddingInline: '0px',
        }}
        onClick={() => {
          (handleApplyDiscount || handleApplyDiscountDefault)({
            token,
            item,
            onApplyError,
            isManual: true,
          });
        }}
      />
    );
  };

  return (
    <>
      {isOpenDiscountModal && (
        <DiscountModal
          isOpen={isOpenDiscountModal}
          discount={item}
          onClose={handleOnCloseDiscountModal}
          // An already-applied discount must not be re-appliable from its details
          // modal. Withhold the apply affordance (same hide-by-omit-onClick idiom
          // the applied-section modal uses), so FooterActions drops the Apply
          // branch and shows only Close. Other discounts keep the apply flow.
          onClick={isDiscountAppliedMarked ? undefined : handleOnClick}
          onApplyError={onApplyError}
          handleApplyDiscount={
            isDiscountAppliedMarked ? undefined : handleApplyDiscount
          }
          inactive={inactive}
          isFx={isFx}
        />
      )}

      <DiscoutTicketStyled
        data-testid={`discount-ticket-${code}`}
        data-inactive={String(Boolean(inactive))}
        $isComingSoon={isComingSoon}
        $backgroundImage={discountTicketBackground[status]}
        $inactive={inactive}
        style={styles}
      >
        <CircleRoundCut isBorderDashedLine={isComingSoon} />
        <DiscountTicketContent>
          <DiscountTicketContentColLeft onClick={handleOnOpenDiscountModal}>
            <DescText
              style={{
                color: textColor()[status],
              }}
            >
              {shortDescription}
            </DescText>
            <SizeBox height={4} />
            <ContentColLeftBottom
              style={
                isActiveUnlimited && lastRedeemAt
                  ? endDate
                    ? {}
                    : {
                        display: 'flex',
                        justifyContent: 'start',
                        alignItems: 'center',
                      }
                  : {}
              }
            >
              {!isDiscountActiveUsedHidden ? (
                <ChipWrap style={chipStyles()[status]}>
                  <ChipText>
                    {
                      getChipValue({
                        t,
                        redeem,
                        limitTxnPerCustomer,
                      })[status]
                    }
                  </ChipText>
                </ChipWrap>
              ) : (
                <></>
              )}
              {isActiveUnlimited && lastRedeemAt && (
                <DiscountExpired>
                  <ExpiredText
                    style={{
                      color: textColor()[status],
                    }}
                  >
                    {`${t(
                      'label_offers_last_use',
                    )} ${datePicker.convertToLocalDateTimeWithTimeZone(
                      new Date(lastRedeemAt),
                    )}`}
                  </ExpiredText>
                </DiscountExpired>
              )}
              <DiscountExpired>
                {endDate && (
                  <ExpiredText
                    style={{
                      color: textColor()[status],
                    }}
                    dangerouslySetInnerHTML={{
                      __html: getValidText({
                        t,
                        startDate,
                        endDate,
                        lastRedeemAt,
                      })[status],
                    }}
                  />
                )}
                {noteIco[status]}
              </DiscountExpired>
            </ContentColLeftBottom>
          </DiscountTicketContentColLeft>
          <DiscountTicketContentColRight>
            <ContentColRightBody $isActive={isActiveAndAvailable}>
              <ScaleTextContainer>
                <DiscountAmountUnitText
                  className={discountAmountUnitClassName}
                  style={{
                    color: textTicketColor()[status],
                  }}
                >
                  {format.toAmountStr(Number(discountAmount), false)}
                  <span>{getDiscountUnit(discountMethod)}</span>
                </DiscountAmountUnitText>
              </ScaleTextContainer>
              <DiscountMethodText
                style={{
                  color: textTicketColor()[status],
                }}
              >
                {stringUtils.capitalize(t(discountMethodKey))}
              </DiscountMethodText>
              {isActiveAndAvailable && (
                <>
                  {isCreateTxn ? (
                    <>{renderButtonApply()}</>
                  ) : (
                    <ButtonCommon
                      value={isFx ? t('button_buy') : t('button_send')}
                      color="var(--c-primary)"
                      background="var(--bg-primary)"
                      isFill={true}
                      styles={{
                        paddingInline: 0,
                        height: '32px',
                        whiteSpace: 'nowrap',
                      }}
                      buttonIconStyles={{
                        float: 'right',
                        height: '32px',
                        gap: '4px',
                        maxWidth: '82.58px',
                        minWidth: '82.58px',
                        // paddingInline: locate === VI_LANG && '0px',
                      }}
                      iconSrc={isFx ? BuyIcon : SendIcon}
                      onClick={() => {
                        Cookies.remove(MAKE_PAYMENT_KEY);
                        Cookies.remove(FX_MAKE_PAYMENT_KEY);
                        Cookies.remove(ORDER_EMAIL);
                        handleOnClick();
                      }}
                    />
                  )}
                </>
              )}
            </ContentColRightBody>
          </DiscountTicketContentColRight>
        </DiscountTicketContent>
      </DiscoutTicketStyled>
    </>
  );
};

const DiscoutTicketStyled = styled.div`
  position: relative;
  margin-top: 8px;
  background-image: ${(props) => `url(${props.$backgroundImage})`};
  background-size: cover;
  height: 100px;
  border-radius: 12px;
  filter: ${(props) => props.$inactive && 'var(--ds-f-discount-inactive)'};

  ${(props) =>
    props.$isComingSoon &&
    `
        border: 2px dashed #A4B2CF;
  `}

  &:first-child {
    margin-top: 0px};
  }

  @media screen and (min-width: ${MOBILE_SMALL_MIN_WIDTH}px) and (max-width: ${MOBILE_SMALL_MAX_WIDTH}px) {
    height: 108px;
  }
`;

const DiscountTicketContent = styled.div`
  display: flex;
  justify-content: space-between;
  height: 100%;
`;
const DiscountTicketContentColLeft = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  margin-left: 16px;
  padding-block: 5px;
  height: 100%;
  width: calc(100% - 132px);
  cursor: pointer;

  @media screen and (min-width: ${MOBILE_SMALL_MIN_WIDTH}px) and (max-width: ${MOBILE_SMALL_MAX_WIDTH}px) {
    margin-left: 8px;
  }
`;
const DiscountTicketContentColRight = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding-block: 8px;
  margin-right: 8px;
  height: 100%;
  width: ${COL_RIGHT_WIDTH}px;
`;
const ContentColRightBody = styled.div`
  display: grid;
  height: 100%;
  align-content: ${(props) => (props.$isActive ? 'end' : 'center')};
  justify-items: center;
`;

const DescText = styled.p`
  ${paragraphMixin};
  ${textEllipsisSecondLineMixin};
  text-align: left;
`;

const ContentColLeftBottom = styled.div``;
const ChipWrap = styled.div``;
const ChipText = styled.p`
  ${paragraphMixin};
  text-align: center;
  font-size: 12px;
  line-height: normal;
  color: #fff;
  font-weight: 600;
`;

const DiscountExpired = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
`;
const ExpiredText = styled.p`
  ${paragraphMixin};
  text-align: left;
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
`;

const ScaleTextContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: ${COL_RIGHT_WIDTH}px;
  height: 32px;
`;
const DiscountAmountUnitText = styled.p`
  ${paragraphMixin};
  text-align: center;
  font-size: 32px;
  line-height: 1;

  & span {
    font-size: 16px;
  }
`;
const DiscountMethodText = styled.p`
  ${paragraphMixin};
  text-align: center;
  font-size: 12px;
  font-weight: 500;
`;

const NoteIcon = styled.img`
  margin-left: 8px;
  cursor: pointer;
`;

export default DiscountTicket;
